/**
 * App.tsx
 *
 * Root router with auth-aware route guards.
 *
 * ProtectedRoute — requires authentication (valid JWT).
 *   Redirects to / if not authenticated.
 *
 * CoupleRoute — requires authentication AND a couple_id in the JWT.
 *   Redirects to /create if authenticated but no couple yet.
 */
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import IosInstallBanner from './components/ios-install-banner'
import { useAppVersion } from './hooks/use-app-version'
import AnniversarySetupPage from './pages/anniversary-setup'
import CountdownCreatePage from './pages/countdown-create'
import CountdownListPage from './pages/countdown-list'
import CreateCouplePage from './pages/create-couple'
import HomePage from './pages/home'
import JoinCouplePage from './pages/join-couple'
import LoginPage from './pages/login'
import MilestoneCreatePage from './pages/milestone-create'
import MilestoneEditPage from './pages/milestone-edit'
import MilestoneTimelinePage from './pages/milestone-timeline'
import NoteComposePage from './pages/note-compose'
import NoteDetailPage from './pages/note-detail'
import NoteInboxPage from './pages/note-inbox'
import NoteSentPage from './pages/note-sent'
import RegisterPage from './pages/register'
import SettingsPage from './pages/settings'
import WelcomePage from './pages/welcome'
import WishlistDonePage from './pages/wishlist-done'
import WishlistHomePage from './pages/wishlist-home'
import WishlistVibePage from './pages/wishlist-vibe'
import { getAuthPayload, isAuthenticated } from './store/auth'

// ---------------------------------------------------------------------------
// Route Guards
// ---------------------------------------------------------------------------

/**
 * Requires a valid JWT.  If not authenticated → /
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthenticated()) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Requires a valid JWT AND couple_id set.
 * Authenticated but no couple → /create
 */
const CoupleRoute = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthenticated()) return <Navigate to="/" replace />
  const payload = getAuthPayload()
  if (!payload?.couple_id) return <Navigate to="/create" replace />
  return <>{children}</>
}

// ---------------------------------------------------------------------------
// ScrollReset — must live inside BrowserRouter to use useLocation()
// ---------------------------------------------------------------------------

function ScrollReset() {
  useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  })
  return null
}

// ---------------------------------------------------------------------------
// UpdateBanner
// ---------------------------------------------------------------------------

function UpdateBanner({ onReload }: { onReload: () => void }) {
  const { needsUpdate } = useAppVersion()

  if (!needsUpdate) return null

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-[#D4537E] text-white px-4 py-2 flex items-center justify-between gap-3 animate-slide-up"
      style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}
    >
      <span className="font-['DM_Sans'] text-[13px] font-medium">New version available! 💕</span>
      <button
        type="button"
        onClick={onReload}
        className="shrink-0 h-[30px] px-3 rounded-full bg-white/20 font-['DM_Sans'] text-[12px] font-medium active:scale-95 transition-all duration-150"
      >
        Update
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  const handleReload = () => {
    navigator.serviceWorker?.ready.then((reg) => reg.active?.postMessage({ type: 'SKIP_WAITING' }))
    window.location.reload()
  }

  return (
    <BrowserRouter>
      <ScrollReset />
      <UpdateBanner onReload={handleReload} />
      <Routes>
        {/* Public */}
        <Route path="/" element={<WelcomePage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected — auth required, no couple needed */}
        <Route
          path="/create"
          element={
            <ProtectedRoute>
              <CreateCouplePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/join"
          element={
            <ProtectedRoute>
              <JoinCouplePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/anniversary"
          element={
            <ProtectedRoute>
              <AnniversarySetupPage />
            </ProtectedRoute>
          }
        />

        {/* Couple routes — auth + couple_id required */}
        <Route
          path="/home"
          element={
            <CoupleRoute>
              <HomePage />
            </CoupleRoute>
          }
        />

        {/* Phase 3 — Notes */}
        <Route
          path="/notes"
          element={
            <CoupleRoute>
              <NoteInboxPage />
            </CoupleRoute>
          }
        />
        <Route
          path="/notes/compose"
          element={
            <CoupleRoute>
              <NoteComposePage />
            </CoupleRoute>
          }
        />
        <Route
          path="/notes/sent"
          element={
            <CoupleRoute>
              <NoteSentPage />
            </CoupleRoute>
          }
        />
        <Route
          path="/notes/:id"
          element={
            <CoupleRoute>
              <NoteDetailPage />
            </CoupleRoute>
          }
        />

        {/* Phase 4 — Milestones */}
        <Route
          path="/milestones"
          element={
            <CoupleRoute>
              <MilestoneTimelinePage />
            </CoupleRoute>
          }
        />
        <Route
          path="/milestones/new"
          element={
            <CoupleRoute>
              <MilestoneCreatePage />
            </CoupleRoute>
          }
        />
        <Route
          path="/milestones/edit/:id"
          element={
            <CoupleRoute>
              <MilestoneEditPage />
            </CoupleRoute>
          }
        />

        {/* Phase 4 — Countdowns */}
        <Route
          path="/countdowns"
          element={
            <CoupleRoute>
              <CountdownListPage />
            </CoupleRoute>
          }
        />
        <Route
          path="/countdowns/new"
          element={
            <CoupleRoute>
              <CountdownCreatePage />
            </CoupleRoute>
          }
        />

        {/* Phase 5 — Wishlist */}
        <Route
          path="/wishlist"
          element={
            <CoupleRoute>
              <WishlistHomePage />
            </CoupleRoute>
          }
        />
        <Route
          path="/wishlist/done"
          element={
            <CoupleRoute>
              <WishlistDonePage />
            </CoupleRoute>
          }
        />
        <Route
          path="/wishlist/:vibe"
          element={
            <CoupleRoute>
              <WishlistVibePage />
            </CoupleRoute>
          }
        />

        {/* Phase 5/6 — Settings */}
        <Route
          path="/settings"
          element={
            <CoupleRoute>
              <SettingsPage />
            </CoupleRoute>
          }
        />
      </Routes>
      <IosInstallBanner />
    </BrowserRouter>
  )
}
